import React, { useState, useRef } from "react";
import { motion } from "framer-motion";

import { styles } from "../styles";
import { SectionWrapper } from "../hoc";
import { fadeIn, textVariant } from "../utils/motion";
import { testimonials } from "../constants";

const FeedbackCard = ({
  index,
  testimonial,
  name,
  designation,
  company,
  image,
  isScreenshot = false,
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showZoom, setShowZoom] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!imgRef.current || !containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // Get mouse position relative to container
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Calculate percentage position (0-100)
    const xPercent = Math.min(100, Math.max(0, (x / rect.width) * 100));
    const yPercent = Math.min(100, Math.max(0, (y / rect.height) * 100));
    
    setPosition({ x: xPercent, y: yPercent });
  };

  return (
    <motion.div
      variants={fadeIn("", "spring", index * 0.5, 0.75)}
      className='bg-black-200 p-8 rounded-3xl w-full max-w-md mx-auto flex flex-col'
    >
      {isScreenshot ? (
        <div className="w-full mb-6 rounded-xl overflow-hidden shadow-lg">
          <div 
            ref={containerRef}
            className="relative w-full cursor-zoom-in"
            onMouseEnter={() => setShowZoom(true)}
            onMouseLeave={() => setShowZoom(false)}
            onMouseMove={handleMouseMove}
          >
            <div className="relative w-full overflow-hidden">
              <img
                ref={imgRef}
                src={image}
                alt={`screenshot-from-${name}`}
                className={`w-full h-auto transition-transform duration-300 ${
                  showZoom ? 'scale-150' : 'scale-100'
                }`}
                style={{
                  transformOrigin: `${position.x}% ${position.y}%`,
                  maxWidth: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </div>
            {showZoom && (
              <div 
                className="absolute pointer-events-none w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-lg"
                style={{
                  left: `calc(${position.x}% - 2rem)`,
                  top: `calc(${position.y}% - 2rem)`,
                  transform: 'translateZ(0)',
                }}
              >
                <img
                  src={image}
                  alt="Magnified view"
                  className="w-full h-full object-cover"
                  style={{
                    transform: `scale(1.8)`,
                    transformOrigin: `${position.x}% ${position.y}%`,
                  }}
                />
              </div>
            )}
          </div>
          <div className="bg-black-300 p-4">
            <p className='text-white text-sm italic mb-2'>"{testimonial}"</p>
            <div className='flex items-center gap-3'>
              <div className='flex-1'>
                <p className='text-white font-medium text-base'>{name}</p>
                <p className='text-secondary text-xs'>
                  {designation}, {company}
                </p>
              </div>
              <div className='w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg'>
                {name.charAt(0)}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className='text-white font-black text-4xl mb-2'>"</p>
          <p className='text-white tracking-wider text-base mb-6 flex-1'>{testimonial}</p>
          <div className='flex items-center gap-4 pt-4 border-t border-gray-800'>
            <div className='w-12 h-12 rounded-full bg-blue-500 flex-shrink-0 overflow-hidden'>
              <img
                src={image}
                alt={name}
                className='w-full h-full object-cover'
              />
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-white font-medium text-base truncate'>{name}</p>
              <p className='text-secondary text-sm truncate'>{designation}</p>
              <p className='text-secondary text-xs truncate'>{company}</p>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};

const Feedbacks = () => {
  return (
    <div className={`mt-12 bg-black-100 rounded-[20px]`}>
      <div
        className={`bg-tertiary rounded-2xl ${styles.padding} min-h-[300px]`}
      >
        <motion.div variants={textVariant()}>
          <p className={styles.sectionSubText}>What others say</p>
          <h2 className={styles.sectionHeadText}>Testimonials.</h2>
        </motion.div>
      </div>
      <div className={`-mt-20 pb-14 ${styles.paddingX} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto`}>
        {testimonials.map((testimonial, index) => (
          <FeedbackCard key={testimonial.name} index={index} {...testimonial} />
        ))}
      </div>
    </div>
  );
};

export default SectionWrapper(Feedbacks, "");
